import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { reportError, reportWarning } from '@/lib/reporting';

interface Profile {
  id: string;
  user_id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  favorite_grinder: string | null;
  created_at: string;
  updated_at: string;
}

// Result type for profile fetching that distinguishes error types
interface FetchProfileResult {
  profile: Profile | null;
  error: 'network' | 'not_found' | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (email: string, password: string, username: string, roasteryName?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  updateEmail: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (password: string) => Promise<{ error: Error | null }>;
  deleteAccount: () => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Track if user just signed up to use longer retry delays for profile creation
  const justSignedUp = useRef(false);

  // Fetch profile with error type distinction
  const fetchProfile = async (userId: string): Promise<FetchProfileResult> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        reportError(error, { context: 'fetchProfile' });
        return { profile: null, error: 'network' };
      }
      
      return { 
        profile: data as Profile | null, 
        error: data ? null : 'not_found' 
      };
    } catch (err) {
      reportError(err, { context: 'fetchProfile', type: 'network' });
      return { profile: null, error: 'network' };
    }
  };
  
  // Helper to fetch profile for external use (maintains backwards compatibility)
  const fetchProfileData = async (userId: string): Promise<Profile | null> => {
    const result = await fetchProfile(userId);
    return result.profile;
  };

  const refreshProfile = async () => {
    if (user) {
      const profileData = await fetchProfileData(user.id);
      setProfile(profileData);
    }
  };

  useEffect(() => {
    // Helper to validate session by checking if profile exists
    // Uses retry logic with exponential backoff to handle race conditions
    // when the database trigger hasn't created the profile yet
    const validateSession = async (userId: string, retryCount = 0): Promise<boolean> => {
      // Use longer delays for new signups since the trigger needs time
      const isNewSignup = justSignedUp.current;
      const maxRetries = isNewSignup ? 5 : 3;
      const retryDelays = isNewSignup 
        ? [500, 1000, 1500, 2000, 3000] // Longer delays for new signups
        : [300, 600, 1200]; // Shorter delays for existing users
      
      const result = await fetchProfile(userId);
      
      if (result.error === 'network') {
        // Network errors should not sign out the user - report and continue
        reportWarning('Network error fetching profile, will retry on next auth state change');
        return false;
      }
      
      if (result.error === 'not_found') {
        if (retryCount < maxRetries) {
          // Wait and retry - profile trigger may not have completed yet
          const delay = retryDelays[retryCount];
          await new Promise(resolve => setTimeout(resolve, delay));
          return validateSession(userId, retryCount + 1);
        }
        
        // All retries exhausted - profile truly doesn't exist
        reportWarning('Session invalid: profile not found after retries. Signing out...');
        justSignedUp.current = false;
        await supabase.auth.signOut();
        setUser(null);
        setSession(null);
        setProfile(null);
        return false;
      }
      
      // Success - profile found
      justSignedUp.current = false;
      setProfile(result.profile);
      return true;
    };

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Use initial delay to give the database trigger time to run
          // Longer delay for new signups
          const initialDelay = justSignedUp.current ? 300 : 0;
          setTimeout(async () => {
            await validateSession(session.user.id);
          }, initialDelay);
        } else {
          setProfile(null);
        }

        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        setSession(session);
        setUser(session.user);
        await validateSession(session.user.id);
      } else {
        setSession(null);
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, username: string, roasteryName?: string) => {
    // Mark that we just signed up to use longer retry delays for profile creation
    justSignedUp.current = true;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          username,
          display_name: username,
          // Store the original roastery name for pre-filling onboarding
          roastery_name: roasteryName || username,
        },
      },
    });

    if (error) {
      // Reset the flag if signup failed
      justSignedUp.current = false;
    }

    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
  };

  const updateEmail = async (email: string) => {
    const { error } = await supabase.auth.updateUser({ email });
    return { error: error as Error | null };
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error: error as Error | null };
  };

  const deleteAccount = async () => {
    if (!user) {
      return { error: new Error('No user logged in') };
    }

    try {
      // Delete user's profile first (this should cascade to related data)
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('user_id', user.id);

      if (profileError) {
        reportError(profileError, { context: 'deleteAccount' });
        return { error: profileError as Error };
      }

      // Sign out the user (account deletion requires admin API or edge function)
      await signOut();
      
      return { error: null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const value = {
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    refreshProfile,
    updateEmail,
    updatePassword,
    deleteAccount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
